import { Box, Grid, Text } from '@chakra-ui/react'
import { FC, useEffect, useMemo } from 'react'
import { BiPackage } from 'react-icons/bi'
import { FiEdit3, FiTrendingUp, FiTrendingDown } from 'react-icons/fi'
import ReactPaginate from 'react-paginate'

import usePedidosCronograma from '@hook/mrp/use-pedidos-cronograma'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import InfoCard from '@component/Layout/Card/Info'
import CardSlider from '@component/Layout/CardSlider'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'
import PedidosCronogramaTable from '@component/Modules/Mrp/PedidosCronograma/Table'

const PedidosCronogramaView: FC = () => {
  const { menuToggle } = useLayout()
  const { hasSessions } = useSessions()

  const {
    data,
    rawCount,
    loading,
    exporting,
    error,
    params,
    hasAppliedFilters,
    filters,
    setFilters,
    query,
    setQuery,
    filename,
    setFilename,
    filtersList,
    handleUpdateParams,
    applyFilters,
    clearAllFilters,
    actions,
    pagination,
    handlePageChange,
    getFilters,
    dataCards
  } = usePedidosCronograma()

  useEffect(() => {
    getFilters({
      companies: true,
      centers: true,
      providersForPurchase: true,
      compactCategories: true,
      worlds: true,
      // Combo Compradores com id como valor (filtro compradoresIds)
      solicitantes: true
    })
    // Carga única dos combos: getFilters não é estável e, com deps, entrava
    // em loop quando algum filtro voltava vazio
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 272px)'
          : 'calc(100vh - 241px)'
        : hasSessions
        ? 'calc(100vh - 413px)'
        : 'calc(100vh - 382px)',
    [menuToggle, hasSessions]
  )

  return (
    <Layout title="Pedidos / SKUs do Cronograma">
      <Grid
        w="100%"
        bg="white"
        h={height}
        templateColumns={!filters ? '100%' : '260px calc(100% - 260px)'}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            defaultDate={{
              initialDate: params.initialDate || '',
              finalDate: params.finalDate || ''
            }}
            changedValue={undefined}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={false}
            onFilter={applyFilters}
            onClearAllFilters={clearAllFilters}
          />
        )}

        <Box>
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            // Não desabilita quando só a busca local zerou a página
            isDisabled={loading || exporting || rawCount === 0}
          />

          {loading ? (
            <LayoutLoader
              title="Pesquisando registros..."
              message="Aguarde enquanto buscamos os dados"
            />
          ) : error ? (
            <LayoutEmpty title="Erro ao carregar os dados" message={error} />
          ) : rawCount === 0 ? (
            <LayoutEmpty
              title="Nenhum registro encontrado"
              message={
                hasAppliedFilters
                  ? 'Nenhum registro para os filtros aplicados'
                  : 'Aplique os filtros ao lado e clique em Prosseguir'
              }
            />
          ) : (
            <>
              <CardSlider isFilters={filters}>
                <InfoCard
                  value={pagination.totalRows}
                  title="Total de registros"
                  tooltip="Total de registros encontrados na consulta"
                  icon={<BiPackage color="#1459B4" />}
                  decimals={1}
                  noShadow
                />
                <InfoCard
                  value={dataCards.skusDistintos}
                  title="SKUs distintos"
                  tooltip="Quantidade de SKUs distintos na página atual"
                  icon={<BiPackage color="#308AFF" />}
                  decimals={1}
                  noShadow
                />
                <InfoCard
                  value={dataCards.totalRecomendado}
                  title="Qtd recomendada"
                  tooltip="Soma da quantidade recomendada na página atual"
                  icon={<FiTrendingUp color="#276749" />}
                  decimals={1}
                  noShadow
                />
                <InfoCard
                  value={dataCards.totalDesejado}
                  title="Qtd desejada"
                  tooltip="Soma da quantidade desejada na página atual"
                  icon={<FiTrendingDown color="#C22C31" />}
                  decimals={1}
                  noShadow
                />
                <InfoCard
                  value={dataCards.totalAlterados}
                  title="Alterados"
                  tooltip="Quantidade de registros alterados na página atual"
                  icon={<FiEdit3 color="#DD6B20" />}
                  decimals={1}
                  noShadow
                />
              </CardSlider>

              {/* Info banner */}
              <Box
                px={4}
                py={2}
                mx={4}
                mt={2}
                bg="blue.50"
                borderRadius="md"
                border="1px solid"
                borderColor="blue.100"
              >
                <Text fontSize="11px" color="blue.600">
                  Exibindo a <strong>última recomendação do dia</strong> por
                  SKU/centro. Simulações intermediárias do mesmo dia não são
                  exibidas. A{' '}
                  <strong>busca e a ordenação atuam só na página atual</strong>;
                  o Exportar CSV traz todas as páginas do filtro aplicado, sem a
                  busca.
                </Text>
                {exporting && (
                  <Text fontSize="11px" color="blue.700" fontWeight="bold">
                    Exportando todas as páginas...
                  </Text>
                )}
              </Box>

              <Box px={2} pt={1}>
                <PedidosCronogramaTable data={data} height={tableHeight} />
              </Box>

              {/* Server-side pagination */}
              {pagination.totalPages > 1 && (
                <Box mt={1} px={4}>
                  <ReactPaginate
                    previousLabel="Anterior"
                    nextLabel="Próximo"
                    pageCount={pagination.totalPages}
                    forcePage={pagination.actualPage - 1}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    activeClassName="active"
                    containerClassName="pagination"
                    onPageChange={({ selected }) => handlePageChange(selected)}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default PedidosCronogramaView
